<script setup lang="ts">
import { CloseBold, Top } from '@element-plus/icons-vue'

interface AutoSizeConfig {
  maxRows?: number
  minRows?: number
}

const props = withDefaults(defineProps<{
  modelValue?: string
  autoSize?: AutoSizeConfig
  variant?: 'default' | 'updown'
  clearable?: boolean
  loading?: boolean
  readOnly?: boolean
  disabled?: boolean
  placeholder?: string
  submitType?: 'enter' | 'shiftEnter'
}>(), {
  modelValue: '',
  autoSize: () => ({ maxRows: 6, minRows: 2 }),
  variant: 'default',
  clearable: false,
  loading: false,
  readOnly: false,
  disabled: false,
  placeholder: '请输入内容',
  submitType: 'enter',
})

const emit = defineEmits<{
  (e: 'update:modelValue', v: string): void
  (e: 'submit', v: string): void
  (e: 'cancel'): void
  (e: 'change'): void
}>()

const textareaRef = ref<HTMLTextAreaElement | null>(null)
const headerVisible = ref(false)
const inputValue = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

function isSubmitKey(e: KeyboardEvent) {
  return props.submitType === 'enter'
    ? !e.shiftKey && e.key === 'Enter'
    : e.shiftKey && e.key === 'Enter'
}

function handleKeydown(e: KeyboardEvent) {
  if (isSubmitKey(e)) {
    e.preventDefault()
    if (inputValue.value.trim() && !props.loading && !props.disabled) {
      emit('submit', inputValue.value)
    }
  }
}

function handleInput() { emit('change') }
function handleClear() { inputValue.value = ''; textareaRef.value?.focus() }
function handleCancel() { emit('cancel') }

function openHeader() { headerVisible.value = true }
function closeHeader() { headerVisible.value = false }
function focus() { nextTick(() => { textareaRef.value?.focus() }) }
function blur() { textareaRef.value?.blur() }

const inputInstance = computed(() => {
  const el = textareaRef.value?.$el || textareaRef.value
  if (el instanceof HTMLTextAreaElement) return el
  return el?.querySelector?.('textarea') ?? null
})
const rootEl = ref<HTMLElement | null>(null)

defineExpose({ openHeader, closeHeader, focus, blur, inputInstance, $el: rootEl })
</script>

<template>
  <div ref="rootEl" class="el-sender-wrap" :class="[`el-sender-wrap--${variant}`]">
    <Transition name="el-sender-slide">
      <div v-if="$slots.header || headerVisible" class="el-sender-header">
        <slot name="header" />
      </div>
    </Transition>

    <div class="el-sender-content">
      <div v-if="$slots.prefix && variant === 'default'" class="el-sender-prefix">
        <slot name="prefix" />
      </div>

      <el-input
        ref="textareaRef"
        v-model="inputValue"
        type="textarea"
        class="el-sender-textarea"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readOnly"
        :autosize="autoSize"
        resize="none"
        @keydown="handleKeydown"
        @input="handleInput"
      />

      <div :class="variant === 'default' ? 'el-sender-actions' : 'el-sender-updown-actions'">
        <div v-if="$slots.prefix && variant === 'updown'" class="el-sender-prefix">
          <slot name="prefix" />
        </div>

        <div v-if="variant !== 'updown' || $slots.prefix" style="flex: 1" />

        <div class="el-sender-actions">
          <slot name="suffix" />

          <el-button
            v-if="clearable && inputValue && !loading"
            class="el-sender-action-btn"
            link
            circle
            size="small"
            :disabled="disabled"
            @click="handleClear"
          >
            <el-icon><CloseBold /></el-icon>
          </el-button>

          <el-button
            v-if="!loading"
            class="el-sender-send-btn"
            type="primary" circle size="small"
            :disabled="!inputValue.trim() || disabled"
            @click="emit('submit', inputValue)"
          >
            <el-icon><Top /></el-icon>
          </el-button>

          <el-button
            v-if="loading"
            class="el-sender-cancel-btn"
            type="warning" size="small"
            @click="handleCancel"
          >
            停止
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.el-sender-wrap {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid var(--el-border-color-light);
  background: var(--el-bg-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 10px 14px;
  transition: all 0.2s ease;

  @supports (backdrop-filter: blur(8px)) {
    background: var(--el-bg-color);
  }

  &:hover {
    border-color: var(--el-border-color);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }

  &:focus-within {
    border-color: #1a1a1a;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06),
                0 4px 16px rgba(0, 0, 0, 0.08);
  }
}

.el-sender-wrap--updown .el-sender-content {
  flex-direction: column;
  align-items: stretch;
  gap: 0;
}

.el-sender-header {
  //border-bottom: 1px solid var(--el-border-color-lighter);
  //margin-bottom: 8px;
  //padding-bottom: 8px;
}

.el-sender-content {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.el-sender-prefix {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.el-sender-textarea {
  flex: 1;
  :deep(.el-textarea__inner) {
    border: none !important;
    box-shadow: none !important;
    padding: 4px 0;
    resize: none;
    font-size: 15px;
    line-height: 1.6;
    background: transparent;
    min-height: 44px;
    color: var(--el-text-color-primary);

    &::placeholder {
      color: var(--el-text-color-placeholder);
    }

    &:focus {
      box-shadow: none !important;
    }
  }
}

.el-sender-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-height: 40px;
}

.el-sender-updown-actions {
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--el-border-color-lighter);
}

.el-sender-action-btn {
  min-width: 36px;
  min-height: 36px;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.el-sender-send-btn {
  min-width: 40px;
  min-height: 40px;
  border-radius: 50%;
  background-color: #1a1a1a;
  border-color: #1a1a1a;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #333333;
    border-color: #333333;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  :deep(.el-icon) {
    transform: rotate(90deg);
    font-size: 18px;
  }
}

.el-sender-cancel-btn {
  border-radius: 50%;
  width: 36px;
  height: 36px;
  min-height: 36px;
  padding: 0;
  font-weight: 500;
}

.el-sender-slide-enter-active {
  transition: all 0.25s ease-out;
  overflow: hidden;
}
.el-sender-slide-leave-active {
  transition: all 0.2s ease-in;
  overflow: hidden;
}
.el-sender-slide-enter-from,
.el-sender-slide-leave-to {
  max-height: 0;
  opacity: 0;
}
.el-sender-slide-enter-to,
.el-sender-slide-leave-from {
  max-height: 200px;
  opacity: 1;
}

html.dark {
  .el-sender-wrap {
    background: var(--el-bg-color);
    border-color: var(--el-border-color);

    @supports (backdrop-filter: blur(8px)) {
      background: var(--el-bg-color);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .el-sender-wrap,
  .el-sender-send-btn,
  .el-sender-action-btn,
  .el-sender-slide-enter-active,
  .el-sender-slide-leave-active {
    transition-duration: 0.01ms !important;
  }
}
</style>
